/**
 * Nine-name peer set (F14, plan §3.3).
 *
 * The subject is neither excluded nor pinned: this is the sector's top N by
 * return, and the stock screen HIGHLIGHTS the subject's row when it happens to
 * appear (demo:1046). Both alternatives are worse. Excluding it would make the
 * table's Nth row depend on whether the subject would have qualified; pinning it
 * would silently evict a genuine top-N name, so the table would stop meaning
 * "the sector's leaders" for exactly the readers who need it to.
 *
 * A mid-pack subject therefore does not appear in its own peer list. That is a
 * known gap in the reference product, not a port defect — resolving it is a
 * product decision (a "your rank: 34/61" affordance), not a selector change.
 */
import type { SectorId, Ticker, WindowKey } from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';

export interface PeerRow {
  readonly ticker: Ticker;
  readonly sc: SectorId;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

/** Nulls sort last: an unreported return is not the worst return. */
function byReturnDescending<T extends PeerRow>(window: WindowKey) {
  return (a: T, b: T): number => {
    const x = a.ret[window];
    const y = b.ret[window];
    if (x === null) return y === null ? COLLATOR.compare(a.ticker, b.ticker) : 1;
    if (y === null) return -1;
    /* Ticker tie-break is not multiplied by direction — determinism device only. */
    return y - x || COLLATOR.compare(a.ticker, b.ticker);
  };
}

export function selectPeers<T extends PeerRow>(
  subject: PeerRow,
  rows: readonly T[],
  window: WindowKey,
  limit: number,
): readonly T[] {
  if (limit <= 0) return [];
  return rows
    .filter((r) => r.sc === subject.sc)
    .sort(byReturnDescending(window))
    .slice(0, limit);
}
